import { createTheme } from '@mui/material';
import * as tokens from './designTokens';
import { CommentStatus } from 'constants/commentStatus';
import { MET_Header_Font_Family, MET_Font_Weight, MET_Header_Font_Weight } from './constants';
import type { LikertClassification } from 'models/analytics/surveyResult';

/**
 * Every colour in the app resolves from B.C. Design System tokens.
 *
 * `Palette` is imported directly by ~39 files, so it stays as the public surface — but its values
 * and the MUI theme below now both read from the same tokens, so the two can no longer disagree.
 * The `button`/`hover`/`dashboard`/`icons` groups have no equivalent slot in MUI's palette type,
 * so they live here only.
 */
export const Palette = {
    primary: {
        main: tokens.surfaceColorPrimaryDefault,
        light: tokens.surfaceColorPrimaryHover,
        dark: tokens.surfaceColorPrimaryPressed,
    },
    secondary: {
        main: tokens.supportBorderColorWarning,
        dark: tokens.supportBorderColorWarning,
        light: tokens.surfaceColorBackgroundInfo,
    },
    hover: {
        light: tokens.surfaceColorPrimaryHover,
    },
    text: {
        primary: tokens.typographyColorPrimary,
        secondary: tokens.typographyColorSecondary,
        muted: tokens.typographyColorMuted,
        disabled: tokens.typographyColorDisabled,
        invert: tokens.typographyColorPrimaryInvert,
        danger: tokens.typographyColorDanger,
    },
    action: {
        active: tokens.typographyColorLink,
    },
    info: {
        main: tokens.typographyColorSecondary,
    },
    success: {
        main: tokens.supportBorderColorSuccess,
        light: tokens.supportSurfaceColorSuccess,
        emphasis: tokens.iconsColorSuccess,
        surface: tokens.supportSurfaceColorSuccessSubtle,
    },
    error: {
        main: tokens.supportBorderColorDanger,
        light: tokens.supportSurfaceColorDanger,
    },
    border: {
        default: tokens.surfaceColorBorderDefault,
        medium: tokens.surfaceColorBorderMedium,
        dark: tokens.surfaceColorBorderDark,
        subtle: tokens.surfaceColorBorderSubtle,
    },
    background: {
        default: tokens.surfaceColorBackgroundWhite,
        light: tokens.surfaceColorBackgroundLightGray,
        lightBlue: tokens.surfaceColorBackgroundLightBlue,
        darkBlue: tokens.surfaceColorBackgroundDarkBlue,
        offWhite: tokens.surfaceColorBackgroundOffWhite,
        gray: tokens.surfaceColorBackgroundGray,
        paleBlue: tokens.surfaceColorBackgroundPaleBlue,
        skyBlue: tokens.surfaceColorBackgroundSkyBlue,
    },
    internalHeader: {
        backgroundColor: tokens.surfaceColorBackgroundWhite,
        color: tokens.typographyColorPrimary,
    },
    publicHeader: {
        backgroundColor: tokens.surfaceColorBackgroundWhite,
        color: tokens.typographyColorPrimary,
    },
    button: {
        primary: {
            backgroundColor: tokens.surfaceColorPrimaryButtonDefault,
            hoverBackgroundColor: tokens.surfaceColorPrimaryButtonHover,
            disabledBackgroundColor: tokens.surfaceColorPrimaryButtonDisabled,
            color: tokens.typographyColorPrimaryInvert,
            disabledColor: tokens.typographyColorDisabled,
        },
        secondary: {
            backgroundColor: tokens.surfaceColorSecondaryButtonDefault,
            hoverBackgroundColor: tokens.surfaceColorSecondaryButtonHover,
            disabledBackgroundColor: tokens.surfaceColorSecondaryButtonDisabled,
            color: tokens.typographyColorPrimary,
            disabledColor: tokens.typographyColorDisabled,
        },
        tertiary: {
            backgroundColor: tokens.surfaceColorTertiaryButtonDefault,
            hoverBackgroundColor: tokens.surfaceColorTertiaryButtonHover,
            disabledBackgroundColor: tokens.surfaceColorTertiaryButtonDisabled,
            color: tokens.surfaceColorPrimaryDefault,
            disabledColor: tokens.typographyColorDisabled,
        },
    },
    dashboard: {
        upcoming: {
            bg: tokens.supportSurfaceColorWarning,
            border: tokens.supportBorderColorWarning,
        },
        open: {
            bg: tokens.supportSurfaceColorSuccess,
            border: tokens.supportBorderColorSuccess,
        },
        closed: {
            bg: tokens.supportSurfaceColorDanger,
            border: tokens.supportBorderColorDanger,
        },
        asOf: {
            bg: tokens.supportSurfaceColorWarning,
            border: tokens.dataVizYellowLight,
            label: tokens.supportTypographyColorWarning,
            value: tokens.supportTypographyColorWarningEmphasis,
        },
        exportWarning: {
            bg: tokens.supportBorderColorDanger,
            color: tokens.typographyColorPrimaryInvert,
        },
        exportDisclaimer: {
            bg: tokens.dataVizGray40,
            color: tokens.typographyColorPrimary,
        },
    },
    icons: {
        surveyReady: tokens.iconsColorSuccess,
        warning: tokens.supportIconColorWarning,
    },
    /**
     * Results-dashboard chart colours.
     *
     * Each series is an array of swatches plus a matching array of label colours, so a swatch and
     * the text drawn on top of it stay paired. Index into both with the same
     * `i % series.length` - see `DonutChart`/`RankOrderChart`/`LikertChart`.
     */
    chart: {
        // Donut and other unordered categorical series, in draw order.
        categorical: [
            tokens.dataVizBlueDark,
            tokens.dataVizBlueMedium,
            tokens.dataVizBlueLight,
            tokens.dataVizYellow,
            tokens.dataVizOrange,
            tokens.dataVizNeutral,
            tokens.dataVizGreen,
            tokens.dataVizSalmon,
            tokens.dataVizPurple,
            tokens.dataVizGreenDeep,
        ],
        categoricalLabel: [
            tokens.typographyColorPrimaryInvert,
            tokens.typographyColorPrimaryInvert,
            tokens.typographyColorPrimary,
            tokens.typographyColorPrimary,
            tokens.typographyColorPrimaryInvert,
            tokens.typographyColorPrimary,
            tokens.typographyColorPrimary,
            tokens.typographyColorPrimary,
            tokens.typographyColorPrimary,
            tokens.typographyColorPrimary,
        ],
        // Rank-order stacked bars, indexed by rank position (0 = 1st place). Shares the Likert
        // classification tokens: blues for the top ranks through golds to yellow for 6th.
        rank: [
            {
                fill: tokens.dataVizBlue100,
                border: tokens.dataVizBlue100Border,
                label: tokens.typographyColorPrimaryInvert,
            },
            {
                fill: tokens.dataVizBlue80,
                border: tokens.dataVizBlue80Border,
                label: tokens.typographyColorPrimaryInvert,
            },
            { fill: tokens.dataVizBlue40, border: tokens.dataVizBlue40Border, label: tokens.typographyColorPrimary },
            { fill: tokens.dataVizGold10, border: tokens.dataVizGold10Border, label: tokens.typographyColorPrimary },
            { fill: tokens.dataVizGold60, border: tokens.dataVizGold60Border, label: tokens.typographyColorPrimary },
            { fill: tokens.dataVizYellow, border: tokens.dataVizYellowBorder, label: tokens.typographyColorPrimary },
        ],
        // Rank position medals use the wireframe colours, not the bar palette. 6th+ falls back to `rank`.
        rankMedal: [
            { fill: tokens.dataVizBlueDark, label: tokens.typographyColorPrimaryInvert },
            { fill: tokens.dataVizBlueMedium, label: tokens.typographyColorPrimaryInvert },
            { fill: tokens.dataVizBlueLight, label: tokens.typographyColorPrimary },
            { fill: tokens.dataVizYellowLight, label: tokens.typographyColorPrimary },
            { fill: tokens.dataVizOrange, label: tokens.typographyColorPrimaryInvert },
        ],
        likert: [
            tokens.dataVizRed,
            tokens.dataVizNeutral,
            tokens.dataVizAmber,
            tokens.dataVizBlueSky,
            tokens.dataVizBlueDark,
        ],
        likertLabel: [
            tokens.typographyColorPrimaryInvert,
            tokens.typographyColorPrimary,
            tokens.typographyColorPrimary,
            tokens.typographyColorPrimary,
            tokens.typographyColorPrimaryInvert,
        ],
        likertClassification: {
            neg3: {
                fill: tokens.dataVizYellow,
                border: tokens.dataVizYellowBorder,
                label: tokens.typographyColorPrimary,
            },
            neg2: {
                fill: tokens.dataVizGold60,
                border: tokens.dataVizGold60Border,
                label: tokens.typographyColorPrimary,
            },
            neg1: {
                fill: tokens.dataVizGold10,
                border: tokens.dataVizGold10Border,
                label: tokens.typographyColorPrimary,
            },
            neutral: {
                fill: tokens.dataVizGray40,
                border: tokens.dataVizGray40Border,
                label: tokens.typographyColorPrimary,
            },
            pos1: {
                fill: tokens.dataVizBlue40,
                border: tokens.dataVizBlue40Border,
                label: tokens.typographyColorPrimary,
            },
            pos2: {
                fill: tokens.dataVizBlue80,
                border: tokens.dataVizBlue80Border,
                label: tokens.typographyColorPrimaryInvert,
            },
            pos3: {
                fill: tokens.dataVizBlue100,
                border: tokens.dataVizBlue100Border,
                label: tokens.typographyColorPrimaryInvert,
            },
        } as Record<LikertClassification, { fill: string; border: string; label: string }>,
        likertNotSure: {
            fill: tokens.dataVizNotSureFill,
            border: tokens.dataVizNotSureBorder,
            label: tokens.typographyColorPrimary,
        },
        // Used when a data point falls outside a fixed-length scale.
        fallback: {
            swatch: tokens.dataVizNeutral,
            label: tokens.typographyColorPrimary,
        },
        // Chart chrome: the surfaces rows, bars and callouts are drawn on.
        surface: {
            rowHover: tokens.surfaceColorBackgroundOffWhite,
            rowDivider: tokens.surfaceColorBorderSubtle,
            callout: tokens.surfaceColorBackgroundPaleBlue,
            track: tokens.surfaceColorTrack,
            drawer: tokens.surfaceColorBackgroundGray,
            bar: tokens.surfaceColorBackgroundSkyBlue,
        },
        // Marker on a conditional follow-up question.
        conditionalMarker: tokens.dataVizGreen,
    },
};

/**
 * Banner colours - B.C. Design System alerts: light surface, darker border, primary text.
 *
 * Applied to both the `filled` (app notification snackbar) and `standard` (survey autosave
 * snackbar) variants so the two match. MUI's `filled` variant hardcodes white text and a white
 * icon, so colour and icon colour are both overridden or the text disappears on the light surface.
 */
const alertStyle = (surface: string, border: string, icon: string) => ({
    backgroundColor: surface,
    color: tokens.typographyColorPrimary,
    border: `1px solid ${border}`,
    '& .MuiAlert-icon, & .MuiAlert-action': {
        color: icon,
    },
});

const successAlert = alertStyle(
    tokens.supportSurfaceColorSuccessSubtle,
    tokens.iconsColorSuccess,
    tokens.iconsColorSuccess,
);
// The amber border is too light for the icon on the light surface, so the icon uses the darker amber.
const warningAlert = alertStyle(
    tokens.supportSurfaceColorWarning,
    tokens.supportBorderColorWarning,
    tokens.supportIconColorWarning,
);
const errorAlert = alertStyle(
    tokens.supportSurfaceColorDanger,
    tokens.supportBorderColorDanger,
    tokens.supportBorderColorDanger,
);

export const BaseTheme = createTheme({
    palette: {
        primary: {
            main: Palette.primary.main,
            light: Palette.primary.light,
            dark: Palette.primary.dark,
        },
        secondary: {
            main: Palette.secondary.main,
            dark: Palette.secondary.dark,
            light: Palette.secondary.light,
        },
        text: {
            primary: Palette.text.primary,
            secondary: Palette.text.secondary,
            disabled: Palette.text.disabled,
        },
        action: {
            active: Palette.action.active,
            disabled: tokens.typographyColorDisabled,
            disabledBackground: tokens.surfaceColorPrimaryDisabled,
        },
        info: {
            main: Palette.info.main,
        },
        // Previously undefined, so every validation and error state fell through to MUI's defaults.
        error: {
            main: tokens.supportBorderColorDanger,
            light: tokens.supportSurfaceColorDanger,
        },
        warning: {
            main: tokens.supportBorderColorWarning,
            light: tokens.supportSurfaceColorWarning,
        },
        success: {
            main: tokens.supportBorderColorSuccess,
            light: tokens.supportSurfaceColorSuccess,
        },
        background: {
            default: Palette.background.default,
            paper: Palette.background.default,
        },
        divider: Palette.border.default,
    },
    shape: {
        borderRadius: parseInt(tokens.layoutBorderRadiusMedium, 10),
    },
    components: {
        MuiAlert: {
            styleOverrides: {
                filledSuccess: successAlert,
                standardSuccess: successAlert,
                filledWarning: warningAlert,
                standardWarning: warningAlert,
                filledError: errorAlert,
                standardError: errorAlert,
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    height: '40px',
                    '&.Mui-disabled': {
                        backgroundColor: tokens.surfaceColorPrimaryDisabled,
                        color: tokens.typographyColorDisabled,
                    },
                    '&.Mui-focusVisible': {
                        outline: `2px solid ${tokens.surfaceColorBorderActive}`,
                        outlineOffset: '2px',
                    },
                },
            },
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiTextField: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: tokens.surfaceColorBorderDefault,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: tokens.surfaceColorBorderMedium,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: tokens.surfaceColorBorderActive,
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: tokens.supportBorderColorDanger,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: tokens.surfaceColorFormsDisabled,
                    },
                },
                input: {
                    '&::placeholder': {
                        color: tokens.typographyColorPlaceholder,
                        opacity: 1,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    '&.Mui-error': {
                        color: tokens.typographyColorDanger,
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: tokens.surfaceColorBorderMedium,
                    '&.Mui-checked': { color: tokens.surfaceColorPrimaryDefault },
                    '&.Mui-disabled': { color: tokens.typographyColorDisabled },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: tokens.surfaceColorBorderMedium,
                    '&.Mui-checked': { color: tokens.surfaceColorPrimaryDefault },
                    '&.Mui-disabled': { color: tokens.typographyColorDisabled },
                },
            },
        },
        MuiLink: {
            defaultProps: {
                color: Palette.action.active,
            },
        },
        MuiFormLabel: {
            defaultProps: {
                focused: false,
            },
            styleOverrides: {
                root: {
                    '&.Mui-error': { color: tokens.typographyColorDanger },
                    '&.Mui-disabled': { color: tokens.typographyColorDisabled },
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: {
                    color: `${tokens.typographyColorPrimary} !important`,
                    opacity: '100% !important',
                },
                labelContainer: {
                    color: `${tokens.typographyColorPrimary} !important`,
                    opacity: '100% !important',
                },
            },
        },
    },
    typography: {
        fontFamily: MET_Header_Font_Family,
        fontSize: 16,
        // Sizes and line heights come from the BC DS typography tokens; headings are bold, body regular.
        h1: {
            fontWeight: MET_Header_Font_Weight,
            fontSize: tokens.typographyFontSizeH1,
            lineHeight: tokens.typographyLineHeightH1,
        },
        h2: {
            fontWeight: MET_Header_Font_Weight,
            fontSize: tokens.typographyFontSizeH2,
            lineHeight: tokens.typographyLineHeightH2,
        },
        h3: {
            fontWeight: MET_Header_Font_Weight,
            fontSize: tokens.typographyFontSizeH3,
            lineHeight: tokens.typographyLineHeightH3,
        },
        h4: {
            fontWeight: MET_Header_Font_Weight,
            fontSize: tokens.typographyFontSizeH4,
            lineHeight: tokens.typographyLineHeightH4,
        },
        h5: {
            fontWeight: MET_Header_Font_Weight,
            fontSize: tokens.typographyFontSizeH5,
            lineHeight: tokens.typographyLineHeightH5,
        },
        h6: {
            fontWeight: MET_Header_Font_Weight,
            fontSize: tokens.typographyFontSizeLargeBody,
            lineHeight: tokens.typographyLineHeightLargeBody,
        },
        subtitle1: {
            fontWeight: MET_Font_Weight,
            fontSize: tokens.typographyFontSizeLargeBody,
            lineHeight: tokens.typographyLineHeightLargeBody,
        },
        subtitle2: {
            fontWeight: MET_Font_Weight,
            fontSize: tokens.typographyFontSizeLargeBody,
            lineHeight: tokens.typographyLineHeightLargeBody,
        },
        body1: {
            fontWeight: MET_Font_Weight,
            fontSize: tokens.typographyFontSizeBody,
            lineHeight: tokens.typographyLineHeightBody,
        },
        body2: {
            fontWeight: MET_Font_Weight,
            fontSize: tokens.typographyFontSizeSmallBody,
            lineHeight: tokens.typographyLineHeightSmallBody,
        },
        caption: {
            fontWeight: MET_Font_Weight,
            fontSize: tokens.typographyFontSizeLabel,
            lineHeight: tokens.typographyLineHeightLabel,
        },
        button: {
            fontWeight: MET_Header_Font_Weight,
            fontSize: tokens.typographyFontSizeLargeBody,
            textTransform: 'none',
        },
    },
});

export const ZIndex = {
    footer: BaseTheme.zIndex.drawer + 1,
};

export const statusStyles: Record<number | string, { borderColor: string; background: string }> = {
    [CommentStatus.Pending]: {
        borderColor: tokens.supportBorderColorWarning,
        background: tokens.supportSurfaceColorWarning,
    },
    [CommentStatus.Approved]: {
        borderColor: tokens.supportBorderColorSuccess,
        background: tokens.supportSurfaceColorSuccess,
    },
    [CommentStatus.Rejected]: {
        borderColor: tokens.supportBorderColorDanger,
        background: tokens.supportSurfaceColorDanger,
    },
    [CommentStatus.NeedsFurtherReview]: {
        borderColor: tokens.supportBorderColorInfo,
        background: tokens.supportSurfaceColorInfo,
    },
    resubmitted: {
        borderColor: tokens.surfaceColorBorderMedium,
        background: tokens.surfaceColorBackgroundLightGray,
    },
};
